import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, Send, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

const TYPE_LABELS = {
  venue_page_enquiry: 'Enquiry',
  calculator_estimate: 'Calculator Estimate',
  availability_estimate: 'Availability Estimate'
};

export const OwnerLeads = ({ api }) => {
  const [leads, setLeads] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/owner/leads');
      setLeads(res.data.leads || []);
    } catch {
      toast.error('Failed to load leads');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const passToWvc = async (lead) => {
    try {
      await api.post(`/owner/leads/${lead.id}/pass`);
      toast.success('Lead passed to the WVC team — they will follow up');
      load();
    } catch {
      toast.error('Failed to pass lead');
    }
  };

  if (!leads) return <p className="text-sm text-[#1A3626]/50 py-8 text-center">Loading leads…</p>;

  return (
    <div className="bg-white border border-[#E5DCCF] rounded-2xl overflow-hidden" data-testid="owner-leads">
      <div className="px-6 py-4 border-b border-[#E5DCCF] bg-[#FDFCF8]">
        <h3 className="font-wvc-display text-2xl text-[#0A1710]">Leads & Enquiries</h3>
        <p className="text-xs text-[#1A3626]/60 mt-1">Everyone who enquired or generated an estimate for your venue. Pass a lead to WVC and our team takes over the follow-up.</p>
      </div>
      {leads.length === 0 ? (
        <p className="text-sm text-[#1A3626]/50 py-12 text-center">No leads yet.</p>
      ) : (
        <div className="divide-y divide-[#F3EFEB] max-h-[560px] overflow-y-auto">
          {leads.map((l, i) => (
            <div key={`${l.source}-${l.id}-${i}`} className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`owner-lead-${i}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-[#0A1710]">{l.name || 'Guest'}</p>
                  <span className="text-[10px] uppercase tracking-wider bg-[#F3EFEB] text-[#1A3626]/70 px-2 py-0.5 rounded-full">
                    {TYPE_LABELS[l.type] || l.type}
                  </span>
                </div>
                <p className="text-xs text-[#1A3626]/60 mt-1.5 flex items-center gap-3 flex-wrap">
                  {l.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {l.phone}</span>}
                  {l.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email}</span>}
                  {l.event_dates && <span>Dates: {l.event_dates}</span>}
                  {l.guest_count && <span>{l.guest_count} guests</span>}
                </p>
                {l.message && <p className="text-xs text-[#1A3626]/50 mt-1 line-clamp-2">{l.message}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-[10px] text-[#1A3626]/40">{l.created_at ? new Date(l.created_at).toLocaleDateString('en-IN') : ''}</p>
                {l.source === 'lead' && (
                  l.passed_to_wvc ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 px-2.5 py-1 rounded-full mt-2">
                      <CheckCircle className="w-3 h-3" /> Passed to WVC
                    </span>
                  ) : (
                    <Button size="sm" variant="outline" className="mt-2 rounded-full text-xs" onClick={() => passToWvc(l)} data-testid={`owner-lead-pass-${i}`}>
                      <Send className="w-3 h-3 mr-1" /> Pass to WVC
                    </Button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
